import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipGrid } from '@angular/material/chips';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, shareReplay, skip, startWith } from 'rxjs/operators';

export interface Option {
  name: string;
  value: any;
}

@Component({
  selector: 'forms-va-input-tags',
  templateUrl: './input-tags.component.html',
  styleUrls: ['./input-tags.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTagsComponent),
      multi: true,
    },
  ],
})
export class InputTagsComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('searchInput', { static: true, read: ElementRef }) input: ElementRef;
  @ViewChild('searchInput', { read: MatAutocompleteTrigger, static: true })
  trigger: MatAutocompleteTrigger;

  @ViewChild('chipList', { static: false }) chipList: MatChipGrid;

  onChange: any;
  onTouched: any;

  @Input() options: Option[] = [];
  @Input() tooltip: string;
  @Input() placeholder: string;
  @Input() required: boolean;
  @Input() maxFields: number;
  @Input() allowDuplicates = true;
  @Input() allowCustomTags = false;
  @Input() customTagLength = 50;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() bottomSpacing?: 'default' | 'small' | 'none' | false = 'default';
  @Input() hint?: string;
  @Input() inputPlaceholder?: string = '';

  private innerValue: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private innerValueChanges: Observable<any[]> = this.innerValue.asObservable();

  private subscriptions: Subscription[] = [];

  filteredOptions: Observable<Option[]>;
  inputTerm: Subject<string> = new Subject<string>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filteredOptions = combineLatest([
      this.innerValue.pipe(startWith([])),
      this.inputTerm.pipe(startWith('')),
    ]).pipe(
      map((val) => this.filter(val[0], val[1])),
      shareReplay(1),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.innerValueChanges.pipe(skip(1)).subscribe(() => {
        this.chipList.errorState = this.hasError;
        //this.chipList._markAsTouched();
        this.cdr.detectChanges();
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.inputTerm.next('');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  writeValue(obj: any): void {
    this.innerValue.next(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get value(): any[] {
    return this.innerValue.getValue();
  }

  set value(val: any[]) {
    this.innerValue.next(val);
    if (this.onChange) {
      this.onChange(this.value);
    }
    this.inputTerm.next('');
  }

  remove(index: number): void {
    const newValue = this.value.slice();
    newValue.splice(index, 1);
    this.value = newValue;
  }

  isInOptionArray(searchedOption: any, optionArray: any[] = []): boolean {
    return Boolean(optionArray) && optionArray.some((option) => option === searchedOption.value);
  }

  canInsert(value: any[], searchedOption: Option): boolean {
    return this.allowDuplicates || !this.isInOptionArray(searchedOption, value);
  }

  matchesOptionFilter(searchedOption: Option, filterTerm: string): boolean {
    return (
      searchedOption.name.toUpperCase().indexOf(filterTerm.toUpperCase()) > -1 &&
      this.canInsert(this.value, searchedOption)
    );
  }

  canTermBeCustom(term: string): boolean {
    return this.allowCustomTags && !this.options.some((option) => term === option.name);
  }

  filter(value: any[], term: string): Option[] {
    if (!this.options) {
      return [];
    }

    term = term.trim();
    const filtered: Option[] = this.options.filter((option) => this.matchesOptionFilter(option, term));
    if (this.canTermBeCustom(term)) {
      const customTag: Option = { name: term, value: term };
      const options: Option[] = term && this.canInsert(value, customTag) ? [customTag] : [];
      return options.concat(filtered);
    }
    return filtered;
  }

  getOptionLabelFromValue(value: any): string {
    if (!this.options) {
      return this.allowCustomTags ? value : undefined;
    }
    const opt = this.options.find((option) => option.value === value);
    return opt ? opt.name : this.allowCustomTags ? value : undefined;
  }

  addSelection(value: any): void {
    if (!this.canInsert(this.value, value)) {
      return;
    }
    this.value = [...(this.value || []), value];
    window.setTimeout(() => (this.input.nativeElement.value = ''), 50);
  }

  add(event: any): void {
    if (!event.source.selected) {
      return;
    }
    this.addSelection(event.source.value);
  }

  openAutocomplete(): void {
    if (!this.disabled) {
      this.input.nativeElement.focus();
      this.trigger.openPanel();
    }
  }

  get overMaxFields(): boolean {
    return (this.value || []).length > this.maxFields;
  }

  get underMinFields(): boolean {
    return this.required && (!this.value || this.value.length === 0);
  }

  get hasError(): boolean {
    return this.underMinFields || this.overMaxFields;
  }
}
